import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from '../../utils/axiosInstance';

/** Which kind of pasted product list narrows the Pricy scope. */
export type PricyListKind = 'ean' | 'vendor_code';

export interface PricyScopeInput {
  brand: string;
  product_list_kind: PricyListKind;
  /** Pasted multi-line text — the backend splits on newlines/commas. */
  product_list: string;
}

export interface PricyPreviewResult {
  status: string;
  vendor: string;
  product_number: number;
  product_variant_number: number;
  unmatched_eans?: string[];
  unmatched_vendor_codes?: string[];
  vendor_exists?: boolean;
}

export interface PricyCreateInput extends PricyScopeInput {
  discount_value: number;
  pricy_status: 'Active' | 'Draft';
}

export interface PricyCreateResult {
  created: boolean;
  pricy_campaign_id: number;
  pricy_campaign_url: string | null;
  scope_summaries?: Array<{
    product_number: number;
    product_variant_number: number;
    unmatched_eans?: string[];
    unmatched_vendor_codes?: string[];
  }>;
}

export const usePricyPreview = (campaignId: string) =>
  useMutation({
    mutationFn: async (payload: PricyScopeInput): Promise<PricyPreviewResult> => {
      const res = await axios.post(`api/campaigns/${campaignId}/pricy/preview/`, payload);
      return res.data;
    },
  });

export const useCreatePricyCampaign = (campaignId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: PricyCreateInput): Promise<PricyCreateResult> => {
      const res = await axios.post(`api/campaigns/${campaignId}/pricy/create/`, payload);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaign', campaignId] });
      qc.invalidateQueries({ queryKey: ['campaigns'] });
    },
  });
};
